import React from 'react';
import { openPlaytomicBooking } from '@/utils/playtomicHandoff';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

import { notifyBookingInitiated } from '@/utils/bookingNotifications';

interface PlaytomicBookingButtonProps {
  playtomicTenantId: string;
  slug: string;
  date: string;
  time: string;
  durationMinutes: number;
  clubName?: string;
  matchId?: string | null;
  groupId?: string | null;
  playerIds?: (string | undefined)[];
  matchDate?: string;
  rosterLabel?: string;
  size?: 'default' | 'compact' | 'pill';
  utmMedium?: string;
}

export const PlaytomicBookingButton: React.FC<PlaytomicBookingButtonProps> = ({
  playtomicTenantId,
  slug,
  date,
  time,
  durationMinutes,
  clubName,
  matchId,
  groupId,
  playerIds = [],
  matchDate,
  rosterLabel,
  size = 'default',
  utmMedium = 'match_card',
}) => {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();

  const handleClick = async () => {
    if (user && matchId) {
      try {
        // Check for existing pending record to prevent duplicates
        const { data: existing } = await supabase
          .from('booking_feedback')
          .select('id')
          .eq('match_id', matchId)
          .eq('user_id', user.id)
          .is('booked', null)
          .maybeSingle();

        if (!existing) {
          await supabase.from('booking_feedback').insert({
            user_id: user.id,
            match_id: matchId,
            booking_channel: 'playtomic',
            club_name: clubName || null,
            booked: null,
          });
          queryClient.invalidateQueries({ queryKey: ['match-booking-status', matchId] });
          // Fire-and-forget notification
          notifyBookingInitiated(
            {
              matchId,
              groupId: groupId || null,
              playerIds: playerIds.filter(Boolean) as string[],
              currentUserId: user.id,
              matchDate: matchDate || undefined,
              clubName: clubName || undefined,
              rosterLabel: rosterLabel || undefined,
            },
            profile?.full_name || 'A player'
          );
        }
      } catch (e) {
        console.error('Failed to insert pending booking feedback:', e);
      }
    }

    localStorage.setItem('booking_handoff', JSON.stringify({
      timestamp: Date.now(),
      channel: 'playtomic',
      clubName: clubName || null,
      matchId: matchId || null,
      groupId: groupId || null,
      playerIds: playerIds.filter(Boolean),
      matchDate: matchDate || null,
      rosterLabel: rosterLabel || null,
    }));

    openPlaytomicBooking({
      slug,
      tenantId: playtomicTenantId,
      date,
      time,
      durationMinutes,
      utmMedium,
    });
  };

  // Pill variant — compact inline button for side-by-side layout
  if (size === 'pill') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-[#4ade80]/25 bg-[#4ade80]/5 hover:bg-[#4ade80]/10 transition-colors text-xs font-medium text-foreground"
      >
        <ExternalLink size={13} className="text-[#4ade80]" />
        <span>Playtomic</span>
      </button>
    );
  }

  const isCompact = size === 'compact';

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={handleClick}
        className={`flex items-center gap-3 w-full rounded-lg border border-[#4ade80]/30 bg-[#4ade80]/5 hover:bg-[#4ade80]/10 transition-colors group ${
          isCompact ? 'p-2.5' : 'p-3'
        }`}
      >
        <div className={`flex-shrink-0 rounded-full bg-[#4ade80]/15 text-[#4ade80] ${
          isCompact ? 'p-1.5' : 'p-2'
        }`}>
          <ExternalLink size={isCompact ? 15 : 18} />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className={`font-medium text-foreground leading-tight ${isCompact ? 'text-xs' : 'text-sm'}`}>
            Book on Playtomic
          </p>
          {clubName && (
            <p className={`text-muted-foreground truncate ${isCompact ? 'text-[10px]' : 'text-xs'}`}>
              {clubName}
            </p>
          )}
        </div>
        <span className={`text-[#4ade80] font-medium group-hover:underline whitespace-nowrap ${
          isCompact ? 'text-[10px]' : 'text-xs'
        }`}>
          Open
        </span>
      </button>
    </div>
  );
};
